import { Modal, Tooltip } from 'antd';
import React, { useState, useEffect, useRef } from 'react';
import AlertsSearch from '../alerts/components/AlertsSearch';
import classNames from 'classnames';
import { actions as serviceLineActions } from '../../Slice/ServiceLinesSlice';
import { actions as flurryAction } from '../../Slice/HelperSlice';
import { useDispatch, useSelector } from 'react-redux';
import { Company } from '../../Types/CompaniesTypes';
import InfiniteScroll from 'react-infinite-scroll-component';
import AddCompanyItem from '../companyComparison/components/AddCompanyItem';
import AffinitySearchResultItemSkeleton from '../common/skeleton/AffinitySearchResultItemSkeleton';
import { SearchResultsRequest } from '../../Types/GlobalSearchResultsTypes';
import { getLangCookie, valueToObject } from '../../Utils/UtilityFunctions';
import { actions as globalSearchActions } from '../../Slice/GlobalSearchResultsSlice';
import { companiesData, selectLoading } from '../../Selector/GlobalSearchResultsSelector';
import { selectNewsTabPage, selectServicelineCompanies, selectServicelineCompaniesCount, selectServicelineCompaniesLoading, selectServicelineCompanyAdded, selectServicelineRegions, selectServicelineSelectedRegions, selectServicelineWidgets, selectWidgetLoading } from '../../Selector/ServiceLinesSelector';
import { formatDate, startOfMonth, subtractMonths } from 'Utils/dateUtils';
import { RegionEntry, Widget } from '../../Types/ServiceLinesTypes';
import { Filter } from '../../Types/FilterTypes';
import { Article } from '../../Types/ArticleTypes';

type Props = {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  serviceLineId: number
  widgetName?: string
  filters: Filter[];
};

const defaultProps = {
  showModal: false,
  serviceLineId: 0,
  widgetName: '',
};

const SLAddCompanyModal = function (props: Props) {
  const { showModal, setShowModal, serviceLineId, widgetName, filters } = props;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchPage, setSearchPage] = useState(1);
  const dispatch = useDispatch();
  const SLCompanies: Company[] = useSelector(selectServicelineCompanies);
  const SLCompaniesLoading: boolean = useSelector(selectServicelineCompaniesLoading);
  const SLCompaniesCount: number = useSelector(selectServicelineCompaniesCount);
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
  const [unselectedCompanies, setUnselectedCompanies] = useState<Company[]>([]);
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const searchedCompanies = useSelector(companiesData);
  const searchLoading = useSelector(selectLoading);
  const companyAdded = useSelector(selectServicelineCompanyAdded);
  const [addInitialized, setAddInitialized] = useState<boolean>(false);
  const fetchingRef = useRef(false);
  const fetchingSearchRef = useRef(false);
  const widgetsRefreshedRef = useRef(false);
  const serviceLineWidgets: Widget[] = useSelector(selectServicelineWidgets);
  const widget = serviceLineWidgets.find(((w) => w.title === widgetName));
  const widgetLoading = useSelector(selectWidgetLoading);
  const articles: Article[] = widget?.news || [];
  const selectedRegions = useSelector(selectServicelineSelectedRegions);
  const SLRegionsData: RegionEntry[] = useSelector(selectServicelineRegions);
  const allSubRegions = SLRegionsData.flatMap(item => item.subRegions || []);
  const host = window.location.href;

  useEffect(() => {
    if (showModal && serviceLineId) {
      setSearchTerm('');
      setSearchPage(1);
      setIsSearch(false);
      setSelectedCompanies([]);
      fetchingRef.current = false;
      fetchingSearchRef.current = false;
      widgetsRefreshedRef.current = false;

      dispatch(serviceLineActions.getSLCompanies({
        offset: 0,
        screenName: "addCompanyToServiceLine",
        serviceLineId: serviceLineId,
      }));
    }
  }, [showModal, serviceLineId, dispatch]);

  const fetchMore = () => {
    if (fetchingRef.current) {
      return;
    }

    if (!SLCompaniesLoading && SLCompanies.length < SLCompaniesCount) {
      fetchingRef.current = true;
      dispatch(serviceLineActions.getSLCompanies({
        offset: SLCompanies.length,
        screenName: "addCompanyToServiceLine",
        serviceLineId: serviceLineId,
      }));
    }
  };

  const companyClickHandler = (company: Company, type?: string) => {
    if (type === "isDisabled") return;
    if (type === "add") {
      setSelectedCompanies((prevSelected) => {
        const isAlreadySelected = prevSelected.some((selected) => selected.companyId === company.companyId);
        if (isAlreadySelected) {
          return prevSelected.filter((selected) => selected.companyId !== company.companyId);
        }
        return [...prevSelected, company];
      });
    }
    if (type === "remove") {
      setUnselectedCompanies((prevUnselected) => {
        const isAlreadyUnselected = prevUnselected.some((unselected) => unselected.companyId === company.companyId);
        if (isAlreadyUnselected) {
          return prevUnselected.filter((unselected) => unselected.companyId !== company.companyId);
        }
        return [...prevUnselected, company];
      })
    }
  };

  const onSearching = (searchText: string) => {
    const trimmedValue = searchText.trim();
    setSearchTerm(trimmedValue);
    if (trimmedValue !== '') {
      setSearchPage(1);
      fetchingSearchRef.current = false;
      const obj: SearchResultsRequest = {
        query: `company:${trimmedValue}`,
        screenName: "addCompanyToServiceLine",
        defaultLanguage: getLangCookie(),
        serviceLineId: serviceLineId,
      };
      dispatch(globalSearchActions.globalSearchResults(obj));
      setIsSearch(true);
    }
  };

  const fetchMoreSearchedCompanies = () => {
    if (fetchingSearchRef.current) {
      return;
    }

    if (isSearch && !searchLoading && searchTerm && (searchedCompanies?.companies?.length ?? 0) < (searchedCompanies?.count ?? 0)) {
      fetchingSearchRef.current = true;
      const nextPage = searchPage + 1;
      const obj: SearchResultsRequest = {
        page: nextPage,
        query: `company:${searchTerm}`,
        defaultLanguage: getLangCookie(),
        screenName: "addCompanyToServiceLine",
        serviceLineId: serviceLineId,
      };
      dispatch(globalSearchActions.globalSearchResults(obj));
      setSearchPage(nextPage);
    }
  };

  const saveCompanyHandler = () => {
    const addCompanyIds = selectedCompanies.map((company) => company.companyId);
    const addCompanyStringIds = addCompanyIds.join(',');

    const removeCompanyIds = unselectedCompanies.map((company) => company.companyId);
    const removeCompanyStringIds = removeCompanyIds.join(',');

    dispatch(serviceLineActions.editCompanyInServiceLine({
      serviceLineId: serviceLineId,
      ...(addCompanyStringIds?.length > 0 && { addCompanyIds: addCompanyStringIds }),
      ...(removeCompanyStringIds?.length > 0 && { removeCompanyIds: removeCompanyStringIds }),

    }));

    dispatch(flurryAction.callFlurryEvent(
      valueToObject(
        'ServiceLines_Edit_Company', 0, {
        ...(addCompanyStringIds?.length > 0 && { add_company_ids: addCompanyStringIds }),
        ...(removeCompanyStringIds?.length > 0 && { remove_company_ids: removeCompanyStringIds }),
        host,
      },
      ),
    ))
    setAddInitialized(true);
  };

  const resetDate = () => {
    const start = startOfMonth(subtractMonths(new Date(), 2));
    const end = new Date();

    return {
      start_date: formatDate(start, 'YYYY-MM-DD HH:mm:ss'),
      end_date: formatDate(end, 'YYYY-MM-DD HH:mm:ss'),
      type: 'Last 3 months',
      allTime: false,
    };
  };

  useEffect(() => {
    if (searchTerm === '') {
      setIsSearch(false);
      setSearchPage(1);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (!SLCompaniesLoading) {
      fetchingRef.current = false;
    }
    if (!searchLoading) {
      fetchingSearchRef.current = false;
    }
  }, [SLCompaniesLoading, searchLoading]);

  useEffect(() => {

    if (companyAdded === true && addInitialized) {
      const subRegions = allSubRegions.join(",");
      dispatch(serviceLineActions.refreshAfterAddCompany({
        serviceLineId,
        widgetName: widgetName ?? '',
        dateRangeFilter: resetDate(),
        regions: subRegions,
      }))
      setAddInitialized(false);
      setShowModal(false);
    }
    // if (companyAdded === true && addInitialized && !widgetsRefreshedRef.current) {
    //   widgetsRefreshedRef.current = true;
    //   dispatch(serviceLineActions.getServiceLineWidgets({
    //     id: serviceLineId,
    //   }));
    // }

    // if (!widgetLoading && addInitialized && companyAdded === true && widgetsRefreshedRef.current) {
    //   const dateRangeLast3Months = resetDate();
    //   dispatch(serviceLineActions.setFilters(dateRangeLast3Months));

    //   if (widgetName) {
    //     dispatch(serviceLineActions.getServiceLineDetails({
    //       page: 1,
    //       id: serviceLineId,
    //       widget_name: widgetName,
    //       dateRangeFilter: dateRangeLast3Months,
    //     }));
    //   }
    //   setAddInitialized(false);
    //   setShowModal(false);
    //   widgetsRefreshedRef.current = false;
    // }
  }, [companyAdded, addInitialized, serviceLineId, dispatch, widgetLoading, widgetName]);

  return (
    <Modal
      centered
      open={showModal}
      onOk={() => setShowModal(false)}
      onCancel={() => setShowModal(false)}
      width={350}
      wrapClassName='share-group-with-people-popup service-lines-add-company'
      closable
    >
      <span className='heading-popup'>
        Manage your companies
      </span>
      <div className='company-search-bar'>
        <AlertsSearch
          placeholderText='Search for companies...'
          query={searchTerm}
          loading={searchLoading}
          onSearching={onSearching}
          margin='0px'
          height="36px"
          onClear={() => { setIsSearch(false); setSearchTerm(''); setSearchPage(1); fetchingSearchRef.current = false; }}
        />
      </div>
      <div className='companies-list-section'>
        <span className='header'>
          Companies
        </span>
        <div className='list' id='company-scroll-container'>
          {
            !isSearch ? (
              <div
                id='regular-companies-scroll'
                style={{ height: '280px', overflowY: 'auto', overflowX: 'hidden' }}
                onScroll={(e) => {
                  const target = e.currentTarget;
                  const scrollTop = target.scrollTop;
                  const scrollHeight = target.scrollHeight;
                  const clientHeight = target.clientHeight;
                  const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

                  if (isNearBottom && !SLCompaniesLoading && SLCompanies.length < SLCompaniesCount && !fetchingRef.current) {
                    fetchMore();
                  }
                }}
              >
                <InfiniteScroll
                  hasMore={true}
                  dataLength={SLCompanies.length}
                  next={fetchMore}
                  loader={
                    SLCompaniesLoading ? (
                      <>
                        {[1, 2, 3].map((_, index) => <AffinitySearchResultItemSkeleton key={`loader-${index}`} />)}
                      </>
                    ) : null
                  }
                  className='o-x-hidden add-company-infinite-scroll'
                  scrollableTarget='regular-companies-scroll'
                >
                  {
                    SLCompanies.length > 0 && (
                      <>
                        {
                          SLCompanies.map((company) => (
                            company.isAdminCompany === 0 && company.isAddedToServiceLine === 0 ? (
                              <AddCompanyItem
                                key={company.companyId}
                                companyId={company?.companyId}
                                companyLogo={company?.companyLogo}
                                companyName={company?.companyName}
                                companyPersonality={company?.companyPersonality}
                                companyPulse={company?.companyPulse}
                                companyWebsite={company?.companyWebsite}
                                isChecked={selectedCompanies.some((selectedCompany) => selectedCompany.companyId === company.companyId)}
                                onClick={() => companyClickHandler(company, "add")}
                                tooltip={true}
                                kpmgFlag
                              />
                            ) : company.isAdminCompany === 0 && company.isAddedToServiceLine === 1 && (
                              <AddCompanyItem
                                key={company.companyId}
                                companyId={company?.companyId}
                                companyLogo={company?.companyLogo}
                                companyName={company?.companyName}
                                companyPersonality={company?.companyPersonality}
                                companyPulse={company?.companyPulse}
                                companyWebsite={company?.companyWebsite}
                                isChecked={!unselectedCompanies.some((id) => id.companyId === company.companyId)}
                                onClick={() => { companyClickHandler(company, "remove") }}
                                tooltip={true}
                                kpmgFlag
                              />
                            )
                          ))
                        }
                        {SLCompaniesLoading && (
                          <>
                            {[1, 2, 3].map((_, index) => <AffinitySearchResultItemSkeleton key={`pagination-loader-${index}`} />)}
                          </>
                        )}
                      </>
                    )
                  }
                  {
                    SLCompaniesLoading && SLCompanies.length === 0 && (
                      <>
                        {[1, 2, 3, 4, 5].map((_, index) => <AffinitySearchResultItemSkeleton key={`skeleton-${index}`} />)}
                      </>
                    )
                  }
                </InfiniteScroll>
              </div>
            ) : (
              <div
                id='search-companies-scroll'
                style={{ height: '280px', overflowY: 'auto', overflowX: 'hidden' }}
                onScroll={(e) => {
                  const target = e.currentTarget;
                  const scrollTop = target.scrollTop;
                  const scrollHeight = target.scrollHeight;
                  const clientHeight = target.clientHeight;
                  const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

                  if (isNearBottom && isSearch && !searchLoading && searchTerm && (searchedCompanies?.companies?.length ?? 0) < (searchedCompanies?.count ?? 0) && !fetchingSearchRef.current) {
                    fetchMoreSearchedCompanies();
                  }
                }}
              >
                <InfiniteScroll
                  hasMore={true}
                  dataLength={searchedCompanies?.companies?.length ?? 0}
                  next={fetchMoreSearchedCompanies}
                  loader={
                    searchLoading ? (
                      <>
                        {[1, 2, 3].map((_, index) => <AffinitySearchResultItemSkeleton key={`search-loader-${index}`} />)}
                      </>
                    ) : null
                  }
                  className='o-x-hidden add-company-infinite-scroll'
                  scrollableTarget='search-companies-scroll'
                >
                  {
                    (searchedCompanies?.companies?.length ?? 0) > 0 ? (
                      <>
                        {
                          searchedCompanies?.companies?.map((company) => (
                            company.isAdminCompany === 1 ? (
                              <Tooltip
                                trigger="hover"
                                placement="top"
                                getPopupContainer={(e: HTMLElement) => e}
                                className='triggers-bullet-tooltip person-designation-tip'
                                color={"#fff"}
                                title="Cannot be edit,  Admin access required."
                                arrow={false}
                              >
                                <span className='tooltip-wrapper'>
                                  <AddCompanyItem
                                    key={company.companyId}
                                    companyId={company?.companyId}
                                    companyLogo={company?.companyLogo}
                                    companyName={company?.companyName}
                                    companyPersonality={company?.companyPersonality}
                                    companyPulse={company?.companyPulse}
                                    companyWebsite={company?.companyWebsite}
                                    isChecked={company.isAddedToServiceLine === 1}
                                    onClick={() => companyClickHandler(company, "isDisabled")}
                                    tooltip={true}
                                    kpmgFlag
                                    className='disabled'
                                  />
                                </span>
                              </Tooltip>
                            ) : company?.isAdminCompany === 0 && company?.isAddedToServiceLine === 0 ? (
                              <AddCompanyItem
                                key={company.companyId}
                                companyId={company?.companyId}
                                companyLogo={company?.companyLogo}
                                companyName={company?.companyName}
                                companyPersonality={company?.companyPersonality}
                                companyPulse={company?.companyPulse}
                                companyWebsite={company?.companyWebsite}
                                isChecked={selectedCompanies.some((selectedCompany) => selectedCompany.companyId === company.companyId)}
                                onClick={() => companyClickHandler(company, "add")}
                                tooltip={true}
                                kpmgFlag
                              />
                            ) : company?.isAdminCompany === 0 && company?.isAddedToServiceLine === 1 && (
                              <AddCompanyItem
                                key={company.companyId}
                                companyId={company?.companyId}
                                companyLogo={company?.companyLogo}
                                companyName={company?.companyName}
                                companyPersonality={company?.companyPersonality}
                                companyPulse={company?.companyPulse}
                                companyWebsite={company?.companyWebsite}
                                isChecked={!unselectedCompanies.some((id) => id.companyId === company.companyId)}
                                onClick={() => { companyClickHandler(company, "remove") }}
                                tooltip={true}
                                kpmgFlag

                              />
                            )
                          ))
                        }
                        {searchLoading && (
                          <>
                            {[1, 2, 3].map((_, index) => <AffinitySearchResultItemSkeleton key={`search-pagination-loader-${index}`} />)}
                          </>
                        )}
                      </>
                    ) : searchedCompanies?.companies?.length === 0 && !searchLoading && (
                      <div className='no-result-div'>
                        No results found
                      </div>
                    )
                  }
                  {
                    searchLoading && searchedCompanies?.companies?.length === 0 && (
                      <>
                        {[1, 2, 3, 4, 5].map((_, index) => <AffinitySearchResultItemSkeleton key={`search-skeleton-${index}`} />)}
                      </>
                    )
                  }
                </InfiniteScroll>
              </div>
            )
          }

        </div>
      </div>
      <div className='company-modal-footer'>
        <span
          tabIndex={0}
          role='button'
          className={classNames('company-modal-button add-button', { disabled: selectedCompanies.length === 0 && unselectedCompanies.length === 0 })}
          onClick={() => saveCompanyHandler()}
        >
          Save
        </span>
        <span
          tabIndex={0}
          role="button"
          className={classNames('company-modal-button cancel-button')}
          onClick={() => setShowModal(false)}
        >
          Cancel
        </span>
      </div>
    </Modal>
  );
}
SLAddCompanyModal.defaultProps = defaultProps;
export default SLAddCompanyModal;
